import cx from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'

import { DataTable } from '@/components/data-table'
import ErrorModal from '@/components/error-modal'
import Tooltip from '@/components/tooltip'
import {
  Button,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui'
import { useCampaignClient } from '@/lib/api'
import { CACHE_KEYS, invalidateCampaignQueries } from '@/lib/cache-utils'
import { CampaignStatus } from '@/schema/campaign-details.schema'
import type { GetCampaignFragment } from '@/types/campaign'
import {
  Copy,
  FileArrowDown,
  Files,
  FolderSimpleUser,
  PaperPlaneTilt,
  PencilLine,
  SpinnerGap,
} from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { addDays, format, parseISO, startOfDay } from 'date-fns'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DeleteCampaignModal from '../../components/delete-campaign-modal'
import { PrintFileDownloadModal } from '../../components/print-file-download'
import { stepRoutes } from '../../components/progress-bar'
import { cn } from '@/lib/utils'

enum Status {
  DONE = 'done',
  IN_PROGRESS = 'in_progress',
  UPCOMING = 'upcoming',
  DRAFT = 'draft',
}

export type CampaignTableItem = {
  id: string
  name: string
  type: string
  isDraft: boolean
  startDate: string
  endDate: string
  status: Status
  claimedGifts: string
  updatedAt: string
  completedStep: number
  printFileDownloadLink: string
  printFilePassword: string
  customer: GetCampaignFragment['customer']
  referralCampaign?: boolean
  reuseSameIncentives?: boolean
  campaignStatus: CampaignStatus
}

export default function List() {
  const campaignClient = useCampaignClient()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get query parameters from URL or use defaults
  const [params, setParams] = useState({
    searchTerm: searchParams.get('searchTerm') || '',
    customerId: searchParams.get('customerId') || undefined,
    page: parseInt(searchParams.get('page') || '1', 10),
    itemsPerPage: parseInt(searchParams.get('itemsPerPage') || '10', 10),
    sortBy: searchParams.get('sortBy') || 'updatedAt',
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  })

  // Fetch customers for the filter dropdown
  const { data: customersData } = useQuery({
    queryKey: [CACHE_KEYS.CUSTOMERS],
    queryFn: async () => {
      const response = await campaignClient.customers.query()
      return response.status === 200 ? response.body : []
    },
  })

  // Debounce URL updates to prevent SecurityError
  const updateUrlTimeout = useRef<number | null>(null)
  const prevParamsRef = useRef<string>(JSON.stringify(params))

  // Update URL when params change, with debouncing
  useEffect(() => {
    // Skip if params haven't actually changed
    const currentParamsString = JSON.stringify(params)
    if (currentParamsString === prevParamsRef.current) {
      return
    }

    prevParamsRef.current = currentParamsString

    // Clear previous timeout if it exists
    if (updateUrlTimeout.current) {
      window.clearTimeout(updateUrlTimeout.current)
    }

    // Set new timeout to update URL
    updateUrlTimeout.current = window.setTimeout(() => {
      const urlParams = new URLSearchParams()

      if (params.searchTerm) {
        urlParams.set('searchTerm', params.searchTerm)
      }
      if (params.customerId) {
        urlParams.set('customerId', params.customerId)
      }
      if (params.page !== 1) {
        urlParams.set('page', params.page.toString())
      }
      if (params.itemsPerPage !== 10) {
        urlParams.set('itemsPerPage', params.itemsPerPage.toString())
      }
      if (params.sortBy !== 'updatedAt') {
        urlParams.set('sortBy', params.sortBy)
      }
      if (params.sortOrder !== 'desc') {
        urlParams.set('sortOrder', params.sortOrder)
      }

      setSearchParams(urlParams, { replace: true })
    }, 300) // 300ms debounce

    // Cleanup timeout on unmount
    return () => {
      if (updateUrlTimeout.current) {
        window.clearTimeout(updateUrlTimeout.current)
      }
    }
  }, [params, setSearchParams])

  // Fetch campaigns using ts-rest with pagination and filtering
  const {
    data: campaignsResponse,
    isLoading: loading,
    refetch: campaignRefetch,
  } = useQuery({
    queryKey: [
      CACHE_KEYS.CAMPAIGNS,
      params.page,
      params.itemsPerPage,
      params.searchTerm,
      params.customerId,
      params.sortBy,
      params.sortOrder,
    ],
    queryFn: () =>
      campaignClient.getAll.query({
        query: {
          page: params.page.toString(),
          itemsPerPage: params.itemsPerPage.toString(),
          sortBy: params.sortBy as
            | 'name'
            | 'updatedAt'
            | 'startDate'
            | 'endDate'
            | 'typeOfCampaign'
            | undefined,
          sortOrder: params.sortOrder,
          searchTerm: params.searchTerm || undefined,
          customerId: params.customerId,
        },
      }),
  })

  // Duplicate campaign mutation
  const duplicateCampaignMutation = useMutation({
    mutationFn: (campaignId: string) =>
      campaignClient.duplicate.mutate({
        params: { id: campaignId },
        body: {},
      }),
    onSuccess: () => {
      invalidateCampaignQueries(queryClient)
    },
  })

  // Persistent error surface for execute / duplicate failures. ErrorModal is
  // used instead of a toast because the failure is actionable (e.g.
  // `campaign_has_no_incentives` requires the admin to open the campaign and
  // attach incentives) and a brief toast is too easy to miss while scrolling
  // a long campaign list.
  const [executeError, setExecuteError] = useState<{
    campaignId: string
    message: string
  } | null>(null)

  // Execute campaign mutation
  const executeCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const res = await campaignClient.executeCampaign.mutate({
        params: { id: campaignId },
        body: {},
      })
      if (res.status !== 200) {
        throw res
      }
      return res
    },
    onSuccess: (_, campaignId) => {
      // Record when polling starts so we can ignore stale cached data
      pollingStartedAt.current = Date.now()
      setLoadingExecute(campaignId)
      campaignRefetch()
      invalidateCampaignQueries(queryClient, campaignId)
    },
    onError: (error: unknown, campaignId) => {
      // Surface server-side guards (e.g. campaign_has_no_incentives) so the
      // admin sees the real reason instead of nothing.
      const body = (error as { body?: { error?: string; message?: string } })
        ?.body
      const message = body?.error
        ? t(body.error)
        : body?.message ?? t('failed_to_execute_campaign')
      setExecuteError({ campaignId, message })
    },
  })

  const [loadingExecute, setLoadingExecute] = useState<string>()
  const pollingStartedAt = useRef<number>(0)
  const { data: campaignResponse, dataUpdatedAt } = useQuery({
    queryKey: [CACHE_KEYS.CAMPAIGN, loadingExecute],
    queryFn: async () => {
      if (!loadingExecute) {
        return null
      }
      try {
        return await campaignClient.getById.query({
          params: { id: loadingExecute },
        })
      } catch (error) {
        // If there's an error, stop polling by clearing the loading state
        setLoadingExecute(undefined)
        // Re-throw to let React Query handle it
        throw error
      }
    },
    enabled: !!loadingExecute,
    refetchInterval: loadingExecute ? 5000 : false,
    retry: 1,
  })
  const navigate = useNavigate()

  const { t } = useTranslation()

  // Stop polling and refresh the list when the campaign reaches a final state.
  // The actual backend flow is: readyToCreate → scheduled → active.
  // The spinner shows for 'scheduled' (offline) and loadingExecute (both),
  // so we keep polling until the status leaves 'scheduled'.
  //
  // This must be in a useEffect (not in the render body) because calling
  // setState during render causes React to discard the current render and
  // re-render immediately, which can cause invalidateCampaignQueries'
  // refetch notification to be lost.
  useEffect(() => {
    if (
      !campaignResponse ||
      !('status' in campaignResponse) ||
      campaignResponse.status !== 200 ||
      !('body' in campaignResponse) ||
      !campaignResponse.body ||
      !loadingExecute
    ) {
      return
    }

    // Skip stale cached data from before polling started.
    // Without this check, the useEffect fires immediately with old cached data
    // (e.g., status 'draft' from the edit flow), sees it's not 'scheduled',
    // and stops polling before any fresh data is fetched.
    if (dataUpdatedAt < pollingStartedAt.current) {
      return
    }

    const { campaignStatus, printFilesLink, typeOfCampaign } =
      campaignResponse.body

    const isOffline = typeOfCampaign === 'offline'

    // For offline campaigns, keep polling until printFilesLink is available
    // because the status changes to 'active' before file generation completes.
    // For online campaigns, stop polling once the status leaves 'scheduled'.
    const isReady = isOffline
      ? !!printFilesLink
      : campaignStatus !== CampaignStatus.scheduled

    if (isReady) {
      setLoadingExecute(undefined)
      // Refresh the campaigns list to show updated statuses
      invalidateCampaignQueries(queryClient, loadingExecute)
    }
  }, [campaignResponse, dataUpdatedAt, loadingExecute, queryClient])

  const campaignState = (start: Date, end: Date): Status | undefined => {
    const now = new Date()
    const startDate = new Date(start)
    // endDate is the last day the campaign is still valid
    // We use start of next day instead of endOfDay to avoid timezone shift issues
    const endDateNextDay = startOfDay(addDays(new Date(end), 1))

    if (now >= endDateNextDay) return Status.DONE
    if (startDate <= now && now < endDateNextDay) return Status.IN_PROGRESS
    if (now < startDate) return Status.UPCOMING
  }

  // Transform API response to campaign table items
  const campaigns = useMemo<CampaignTableItem[]>(() => {
    if (campaignsResponse?.status !== 200 || !campaignsResponse?.body?.data) {
      return []
    }

    return campaignsResponse.body.data.map((campaign) => {
      // Ensure status is never undefined by defaulting to DRAFT
      const campaignStatus = campaign.isDraft
        ? Status.DRAFT
        : campaignState(
            new Date(campaign.startDate),
            new Date(campaign.endDate),
          ) || Status.DRAFT

      return {
        id: campaign.id,
        name: campaign.name,
        type: campaign?.typeOfCampaign || '',
        isDraft: campaign.isDraft,
        startDate: format(parseISO(campaign.startDate), 'dd/MM/yyyy'),
        endDate: format(parseISO(campaign.endDate), 'dd/MM/yyyy'),
        status: campaignStatus,
        claimedGifts: '',
        updatedAt: campaign.updatedAt,
        completedStep: campaign.completedStep,
        printFileDownloadLink: campaign.printFilesLink || '',
        printFilePassword: campaign.printPassword || '',
        customer: campaign.customer,
        referralCampaign: !!campaign.referralCampaign,
        // Handle optional properties that might not exist in the API response
        reuseSameIncentives: false, // Default value since it might not be in the API response
        campaignStatus:
          (campaign.campaignStatus as CampaignStatus) || CampaignStatus.draft,
      }
    })
  }, [campaignsResponse])

  const statusClassName = (status: Status) => {
    switch (status) {
      case Status.DONE:
        return 'bg-green-50 text-green-700'
      case Status.IN_PROGRESS:
        return 'bg-violet-30 text-marine-800'
      case Status.UPCOMING:
        return 'bg-marine-300 text-white'
      case Status.DRAFT:
        return 'bg-ui'
    }
  }

  if (loading) return <div>{t('loading')}</div>

  const duplicateCampaign = (campaignId: string) => {
    duplicateCampaignMutation.mutate(campaignId)
  }

  // Define columns for the DataTable
  const columns: ColumnDef<CampaignTableItem>[] = [
    {
      accessorKey: 'name',
      header: () => t('campaign_list.campaign_name'),
      cell: (info) => (
        <span className="text-sm text-gray-1">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'startDate',
      header: () => t('campaign_list.start_date'),
      cell: (info) => (
        <span className="text-sm text-gray-1">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'endDate',
      header: () => t('campaign_list.end_date'),
      cell: (info) => (
        <span className="text-sm text-gray-1">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: () => t('campaign_list.status'),
      cell: (info) => {
        const status = info.getValue() as Status
        return (
          <span
            className={cx(
              'whitespace-nowrap rounded-full px-2.5 text-sm',
              statusClassName(status),
            )}
          >
            {t(`campaign_list.${status}`)}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: () => t('campaign_list.actions'),
      cell: ({ row }) => {
        const campaign = row.original
        return (
          <div className="inline-grid grid-cols-3 gap-2 align-middle w-96">
            <div>
              {
                <button
                  onClick={() =>
                    navigate(
                      stepRoutes(
                        campaign.completedStep,
                        campaign.id,
                        campaign.referralCampaign,
                        campaign.reuseSameIncentives,
                      ),
                    )
                  }
                  className="text-sm flex flex-row gap-2 items-center border rounded-md p-1 w-full"
                >
                  <PencilLine size={16} fill="#490091" />
                  {t('campaign_list.edit')}
                </button>
              }
            </div>
            <div>
              <DeleteCampaignModal campaignToDelete={campaign} />
            </div>
            <div>
              <button
                onClick={() => duplicateCampaign(campaign.id)}
                className="text-sm flex flex-row gap-2 items-center border rounded-md p-1 w-full"
              >
                <Copy size={16} fill="#490091" />
                {t('campaign_list.duplicate')}
              </button>
            </div>
          </div>
        )
      },
    },
    {
      id: 'campaignData',
      header: () => t('campaign_list.campaign_data'),
      cell: ({ row }) => {
        const campaign = row.original
        return (
          <div className="inline-grid grid-cols-2 gap-2 align-middle">
            <div>
              {campaign?.printFileDownloadLink && (
                <ShowDownloadModal campaign={campaign} />
              )}

              {!campaign?.printFileDownloadLink &&
                campaign.type === 'offline' &&
                (campaign.campaignStatus === CampaignStatus.scheduled ||
                campaign.campaignStatus === CampaignStatus.active ||
                loadingExecute === campaign.id ? (
                  <SpinnerGap size={24} className="text-primary animate-spin" />
                ) : (
                  <Tooltip
                    child={
                      <Files
                        className={`${
                          campaign.campaignStatus ===
                          CampaignStatus.readyToCreate
                            ? 'text-primary cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        size={24}
                        onClick={() => {
                          if (
                            campaign.campaignStatus ===
                            CampaignStatus.readyToCreate
                          ) {
                            executeCampaignMutation.mutate(campaign.id)
                          }
                        }}
                      />
                    }
                    text={
                      campaign.campaignStatus === CampaignStatus.draft
                        ? 'The campaign do not have uploaded end users'
                        : t('tooltips.generate_files')
                    }
                  />
                ))}

              {campaign.isDraft &&
                campaign.type === 'online' &&
                (loadingExecute === campaign.id ? (
                  <SpinnerGap size={24} className="text-primary animate-spin" />
                ) : (
                  <Tooltip
                    child={
                      <PaperPlaneTilt
                        size={24}
                        className={`${
                          campaign.campaignStatus ===
                          CampaignStatus.readyToCreate
                            ? 'text-primary cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        onClick={() => {
                          if (
                            campaign.campaignStatus ===
                            CampaignStatus.readyToCreate
                          ) {
                            executeCampaignMutation.mutate(campaign.id)
                          }
                        }}
                      />
                    }
                    text={
                      campaign.campaignStatus === CampaignStatus.draft ||
                      campaign.campaignStatus === CampaignStatus.testing
                        ? 'The campaign do not have uploaded end users'
                        : t('tooltips.send_email')
                    }
                  />
                ))}
            </div>
            <div>
              <a href={`/data/${campaign.id}`} target="_blank">
                <Tooltip
                  child={
                    <FolderSimpleUser
                      size={24}
                      className="text-primary cursor-pointer"
                    />
                  }
                  text={t('tooltips.download_user_data')}
                />
              </a>
            </div>
          </div>
        )
      },
    },
  ]

  // Handle search input change - already debounced by SearchInput component
  const handleSearchChange = (value: string) => {
    // Only update if the value has actually changed
    if (value !== params.searchTerm) {
      setParams((prev) => ({
        ...prev,
        searchTerm: value,
        page: 1, // Reset to first page when searching
      }))
    }
  }

  // Handle customer filter change
  const handleCustomerChange = (value: string) => {
    const newCustomerId = value === 'all' ? undefined : value

    // Only update if the value has actually changed
    if (newCustomerId !== params.customerId) {
      setParams((prev) => ({
        ...prev,
        customerId: newCustomerId,
        page: 1, // Reset to first page when filtering
      }))
    }
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    // Only update if the page has actually changed
    if (newPage !== params.page) {
      setParams((prev) => ({
        ...prev,
        page: newPage,
      }))
    }
  }

  // Get total pages
  const totalItems =
    campaignsResponse?.status === 200
      ? campaignsResponse.body.meta.totalItems
      : 0
  const totalPages = Math.ceil(totalItems / params.itemsPerPage)

  return (
    <div className="bg-ui1 p-8 h-full">
      <div className="p-8 sm:rounded-lg bg-white">
        <h1 className="font-bold text-4xl pb-10 leading-none mb-0">
          {t('campaign_list.campaign_list')}
        </h1>

        {/* Filtering controls */}
        <div className="flex gap-4 mb-6 items-center align-middle">
          <div className="w-full md:w-64">
            <SearchInput
              onSearch={handleSearchChange}
              defaultValue={params.searchTerm || ''}
              placeholder={t('campaign_list.search_placeholder')}
            />
          </div>

          <div className="w-full md:w-64">
            <Select
              value={params.customerId || 'all'}
              onValueChange={handleCustomerChange}
            >
              <SelectTrigger className="shadow-sm">
                <SelectValue
                  placeholder={t('campaign_list.filter_by_customer')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('campaign_list.all_customers')}
                </SelectItem>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped customers payload */}
                {((customersData as any[]) || []).map(
                  (customer: { id: string; name: string }) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border border-border sm:rounded-lg overflow-auto">
          <DataTable columns={columns} data={campaigns} className="w-full" />
        </div>

        {/* Pagination controls */}
        {totalPages > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t('campaign_list.page_info', {
                  currentPage: params.page,
                  totalPages,
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handlePageChange(params.page - 1)}
                disabled={params.page <= 1 || loading}
                variant="outline"
                size="sm"
              >
                {t('campaign_list.previous')}
              </Button>

              {/* Page number buttons */}
              {(() => {
                // Calculate which page numbers to show
                const pageNumbers = []

                // Always show page 1
                pageNumbers.push(1)

                // Add ellipsis if needed
                if (params.page > 3) {
                  pageNumbers.push('ellipsis1')
                }

                // Add pages around current page
                for (
                  let i = Math.max(2, params.page - 1);
                  i <= Math.min(totalPages - 1, params.page + 1);
                  i++
                ) {
                  if (!pageNumbers.includes(i)) {
                    pageNumbers.push(i)
                  }
                }

                // Add ellipsis if needed
                if (params.page < totalPages - 2) {
                  pageNumbers.push('ellipsis2')
                }

                // Always show last page if there is more than one page
                if (totalPages > 1 && !pageNumbers.includes(totalPages)) {
                  pageNumbers.push(totalPages)
                }

                // Render the page buttons
                return pageNumbers.map((pageNum) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={pageNum} className="px-2">
                        ...
                      </span>
                    )
                  }

                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum as number)}
                      variant={pageNum === params.page ? 'default' : 'outline'}
                      size="sm"
                      className={cn(
                        pageNum === params.page
                          ? 'text-white'
                          : 'text-foreground',
                      )}
                    >
                      {pageNum}
                    </Button>
                  )
                })
              })()}

              <Button
                onClick={() => handlePageChange(params.page + 1)}
                disabled={params.page >= totalPages || loading}
                variant="outline"
                size="sm"
              >
                {t('campaign_list.next')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {executeError &&
        createPortal(
          <ErrorModal
            title={t('campaign_list.execute_failed_title')}
            error={executeError.message}
            onClick={() => setExecuteError(null)}
            hasOnSubmit={true}
            onSubmit={() => {
              const id = executeError.campaignId
              setExecuteError(null)
              navigate(`/campaigns/incentives/${id}`)
            }}
            submitButtonText={t('campaign_list.open_campaign')}
          />,
          document.body,
        )}
    </div>
  )
}

function ShowDownloadModal({ campaign }: { campaign: CampaignTableItem }) {
  const { t } = useTranslation()
  const [showPrintFileDownloadModal, setShowPrintFileDownloadModal] =
    useState(false)

  const portal = useMemo(() => {
    if (!showPrintFileDownloadModal) return <></>
    return createPortal(
      <PrintFileDownloadModal
        downloadLink={campaign.printFileDownloadLink}
        password={campaign.printFilePassword}
        onClose={() => setShowPrintFileDownloadModal(false)}
      />,
      document.body,
    )
  }, [campaign, showPrintFileDownloadModal])

  return (
    <>
      {portal}
      <Tooltip
        child={
          <FileArrowDown
            className="text-primary cursor-pointer"
            size={24}
            onClick={() =>
              setShowPrintFileDownloadModal(!showPrintFileDownloadModal)
            }
          />
        }
        text={t('tooltips.download_creative')}
      />
    </>
  )
}
